/*
  BluFood Produktseite (bf E1–E9, E14) – <bf-product> aus sections/bf-main-product.liquid
  - Galerie (Vorschaubilder, Wischen auf dem Handy, Variantenbild)
  - Varianten: Optionen als Pillen, letzte Option als Mengen-Karten, "bester kg-Preis"
  - Lieferhinweis mit Countdown (Logik aus bf-delivery.js, Serverzeit aus bf-cart-watch.js)
  - R77: Vorbestellungs-Hinweis ueber dem Button (Varianten-Feld "Lieferbar ab"), Lieferhinweis dann ausgeblendet
  - Gratis-Versand-Fortschritt inkl. Warenkorb, Meeresperlen wie bisher berechnet
  - In den Warenkorb inkl. Zusatzprodukte (mit Variantenwahl), danach oeffnet der Warenkorb-Drawer
  - Kaufleiste unten (Handy und Desktop), sobald der Warenkorb-Button nach oben aus dem Bild ist
*/
import { createCalendar, readConfig, toISO, toLabel, addDays, zonedNow, parseDate } from '@bf/delivery'
import { onCartUpdate, getLastCart, getServerNow, fetchCart, freeShipCode } from '@bf/cart-watch'

const root = () => ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/')
const currency = () => (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR'
const money = (cents) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency() }).format((cents || 0) / 100)
const cal = createCalendar(readConfig())

/** "1 Std. 37 Min." bzw. "37 Min." */
export function formatDuration (minutes) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h > 0 ? `${h} Std. ${m} Min.` : `${m} Min.`
}

class BfProduct extends HTMLElement {
  connectedCallback () {
    if (this._ready) return
    this._ready = true
    const json = this.querySelector('[data-bf-variants]')
    this.variants = json ? JSON.parse(json.textContent) : []
    this.form = this.querySelector('[data-bf-buy]')
    this.idInput = this.querySelector('[data-bf-variant-input]')
    this.qtyInput = this.querySelector('[data-bf-qty]')
    this.atc = this.querySelector('[data-bf-atc]')
    this.atcText = this.querySelector('[data-bf-atc-text]')
    this.picker = this.querySelector('[data-bf-variant-picker]')
    this.threshold = parseInt(this.dataset.threshold, 10) || 0
    // R61: Grundschwelle ohne Gratis-Versand-Code; data-threshold ist die beim Seitenaufruf wirksame
    this.thresholdBase = parseInt(this.dataset.thresholdBase, 10) || this.threshold
    this.ppe = parseFloat(this.dataset.ppe) || 0
    const startId = this.idInput ? this.idInput.value : ''
    this.current = this.variants.find((v) => String(v.id) === String(startId)) || this.variants[0]

    this.addEventListener('change', (e) => this.onChange(e))
    this.addEventListener('click', (e) => this.onClick(e))
    if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e))
    this.notify = this.querySelector('[data-bf-notify]')
    const notifyForm = this.notify && this.notify.querySelector('form')
    if (notifyForm) notifyForm.addEventListener('submit', (e) => this.onNotify(e, notifyForm))
    // R66: Omnisend kennt nur die Variante beim Seitenaufruf (?variant=) – merken, Scrollposition nach Neuladen wiederherstellen
    this.omniInitial = this.current ? String(this.current.id) : ''
    this.omniInitialSoldOut = !!(this.current && !this.current.available)
    this.restoreBisScroll()

    this.setupGallery()
    this.filterSizes(false)
    this.updateAll()

    onCartUpdate(() => { this.updateShipping(); this.updateDelivery() })
    fetchCart().catch(() => {})
    this.timer = setInterval(() => this.updateDelivery(), 60000)
    this.setupSticky()
  }

  disconnectedCallback () {
    clearInterval(this.timer)
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll)
      window.removeEventListener('resize', this.onScroll)
    }
    document.documentElement.classList.remove('bf-sticky-open')
  }

  /* ---------- Ereignisse ---------- */

  onChange (e) {
    const t = e.target
    if (t.closest('.bf-optgroup')) {
      this.filterSizes(true)
      return
    }
    if (t.name && t.closest('.bf-sizes')) {
      this.selectVariant(t.value)
      return
    }
    if (t.matches('[data-bf-addon-select]')) {
      this.onAddonVariant(t)
      return
    }
    if (t.matches('[data-bf-addon]') || t.matches('[data-bf-qty]')) {
      if (t.matches('[data-bf-qty]')) t.value = Math.max(1, Math.min(99, parseInt(t.value, 10) || 1))
      this.updateTotals()
    }
  }

  onClick (e) {
    const step = e.target.closest('[data-bf-step]')
    if (step && this.qtyInput) {
      const next = (parseInt(this.qtyInput.value, 10) || 1) + parseInt(step.dataset.bfStep, 10)
      this.qtyInput.value = Math.max(1, Math.min(99, next))
      this.updateTotals()
      return
    }
    const thumb = e.target.closest('.bf-gallery__thumb')
    if (thumb) {
      this.showMedia(thumb.dataset.mediaId)
      return
    }
    if (e.target.closest('[data-bf-sticky-btn]') && this.form) {
      if (this.form.requestSubmit) this.form.requestSubmit()
      else this.form.dispatchEvent(new Event('submit', { cancelable: true }))
    }
  }

  /** Zusatzprodukt: andere Variante gewaehlt -> Checkbox, Preis und Haken aktualisieren */
  onAddonVariant (select) {
    const row = select.closest('[data-bf-addon-row]')
    const opt = select.options[select.selectedIndex]
    const cb = row && row.querySelector('[data-bf-addon]')
    if (!cb || !opt) return
    cb.value = opt.value
    cb.dataset.price = opt.dataset.price || '0'
    const price = row.querySelector('[data-bf-addon-price]')
    if (price) price.textContent = '+ ' + (opt.dataset.money || money(parseInt(opt.dataset.price, 10) || 0))
    cb.checked = true
    this.updateTotals()
  }

  /* ---------- Varianten ---------- */

  selectedOthers () {
    return Array.from(this.querySelectorAll('.bf-optgroup')).map((g, i) => {
      let checked = g.querySelector('input:checked')
      if (!checked) {
        // Ohne Vorauswahl die Option der aktuellen Variante waehlen (sonst bliebe die Mengenauswahl leer)
        const want = this.current && Array.isArray(this.current.options) ? this.current.options[i] : null
        const inputs = Array.from(g.querySelectorAll('input'))
        checked = inputs.find((x) => x.value === want) || inputs[0] || null
        if (checked) checked.checked = true
      }
      return checked ? checked.value : ''
    })
  }

  /** Mengen-Karten auf die gewaehlten uebrigen Optionen einschraenken */
  filterSizes (pickNew) {
    if (!this.picker) return
    const others = this.selectedOthers()
    const key = others.length ? others.join('||') + '||' : ''
    const cards = Array.from(this.picker.querySelectorAll('.bf-size'))
    let visible = []
    cards.forEach((card) => {
      const show = (card.dataset.others || '') === key
      card.hidden = !show
      if (show) visible.push(card)
    })
    if (!visible.length) {
      // Sicherheitsnetz: nie eine leere Mengenauswahl zeigen
      visible = cards
      cards.forEach((card) => { card.hidden = false })
    }
    const fieldset = this.picker.querySelector('.bf-sizes')
    if (fieldset) fieldset.dataset.count = String(visible.length)
    this.markBest(visible)
    if (!pickNew) return
    const checked = visible.find((c) => c.querySelector('input').checked)
    const target = checked || visible.find((c) => !c.classList.contains('is-soldout')) || visible[0]
    if (target) {
      const input = target.querySelector('input')
      input.checked = true
      this.selectVariant(input.value)
    }
  }

  markBest (cards) {
    const label = this.picker && this.picker.dataset.bestLabel
    this.picker.querySelectorAll('.bf-size__best').forEach((el) => { el.hidden = true })
    if (!label) return
    // Ab 2 Varianten mit Grundpreis; nur wenn der niedrigste Grundpreis eindeutig ist
    const unit = (c) => parseInt(c.dataset.unit, 10) || 0
    const priced = cards.filter((c) => !c.classList.contains('is-soldout') && unit(c) > 0)
    if (priced.length < 2) return
    const min = Math.min(...priced.map(unit))
    const winners = priced.filter((c) => unit(c) === min)
    if (winners.length !== 1) return
    const tag = winners[0].querySelector('.bf-size__best')
    if (tag) tag.hidden = false
  }

  selectVariant (id) {
    const v = this.variants.find((x) => String(x.id) === String(id))
    if (!v) return
    this.current = v
    if (this.idInput) this.idInput.value = v.id
    try {
      const url = new URL(window.location.href)
      url.searchParams.set('variant', v.id)
      window.history.replaceState(window.history.state, '', url)
    } catch (e) { /* ohne URL-Update weiter */ }
    if (v.media_id) this.showMedia(String(v.media_id))
    this.updateAll()
  }

  /* ---------- Anzeige ---------- */

  updateAll () {
    this.updateTotals()
    this.updateDelivery()
    this.updateLater()
    this.updateNotify()
  }

  /** R77: Vorbestellung – Variante mit Feld "Lieferbar ab" (JSON "later", JJJJ-MM-TT, nur wenn in der Zukunft) */
  laterDate () {
    const v = this.current
    return v && v.available && v.later ? parseDate(v.later) : null
  }

  updateLater () {
    const box = this.querySelector('[data-bf-later]')
    if (!box) return
    const d = this.laterDate()
    box.hidden = !d
    if (!d) return
    const title = box.querySelector('[data-bf-later-title]')
    if (title) title.textContent = String(box.dataset.title || '').replace('{datum}', toLabel(d, true))
  }

  selectionTotal () {
    const qty = this.qtyInput ? Math.max(1, parseInt(this.qtyInput.value, 10) || 1) : 1
    let total = this.current ? this.current.price * qty : 0
    this.querySelectorAll('[data-bf-addon]:checked').forEach((cb) => { total += parseInt(cb.dataset.price, 10) || 0 })
    return total
  }

  updateTotals () {
    const total = this.selectionTotal()
    const available = this.current && this.current.available
    if (this.atc) {
      this.atc.disabled = !available
      if (this.atcText) {
        this.atcText.textContent = available
          ? `${this.atc.dataset.label} · ${money(total)}`
          : this.atc.dataset.soldoutLabel
      }
    }
    // Kaufleiste: gewaehlte Variante und ihr Preis (mal Stueckzahl), ohne Zusatzprodukte
    const qty = this.qtyInput ? Math.max(1, parseInt(this.qtyInput.value, 10) || 1) : 1
    const sticky = this.querySelector('[data-bf-sticky-price]')
    if (sticky && this.current) sticky.textContent = money(this.current.price * qty)
    const stickyBtn = this.querySelector('[data-bf-sticky-btn]')
    if (stickyBtn) stickyBtn.disabled = !available
    const stickyVar = this.querySelector('[data-bf-sticky-variant]')
    if (stickyVar && this.current) {
      const parts = []
      if (qty > 1) parts.push(`${qty} ×`)
      if (this.variants.length > 1) parts.push(this.current.title)
      stickyVar.textContent = parts.join(' ')
      stickyVar.hidden = parts.length === 0
    }
    this.updateSavings()
    this.updateShipping()
    if (this.onScroll) this.onScroll()
  }

  /** "Du sparst …" unter dem Button: Vergleichspreis der gewaehlten Variante mal Stueckzahl */
  updateSavings () {
    const el = this.querySelector('[data-bf-savings]')
    if (!el) return
    const label = this.picker ? this.picker.dataset.saveLabel : ''
    const v = this.current
    const qty = this.qtyInput ? Math.max(1, parseInt(this.qtyInput.value, 10) || 1) : 1
    const diff = v && v.available && v.compare_at_price > v.price ? (v.compare_at_price - v.price) * qty : 0
    el.hidden = !(label && diff > 0)
    el.textContent = diff > 0 && label ? label.split('{betrag}').join(money(diff)) : ''
  }

  /** R61: wirksame Gratis-Versand-Grenze – 1 Cent, solange ein Gratis-Versand-Code eingeloest ist */
  shipThreshold (cart) {
    if (!cart || !this.thresholdBase) return this.threshold
    return freeShipCode(cart) ? 1 : this.thresholdBase
  }

  /** R18: Ueberschrift der Zusatzprodukte – unter der Gratis-Versand-Grenze "Dazu packen ...", sonst "Passend dazu" */
  updateAddonHeading () {
    const h = this.querySelector('[data-bf-addon-heading]')
    if (!h) return
    const cart = getLastCart()
    if (!cart) return // bis der Warenkorb geladen ist, gilt der Wert aus Liquid
    const inCart = cart.total_price || 0
    const qty = this.qtyInput ? Math.max(1, parseInt(this.qtyInput.value, 10) || 1) : 1
    const main = this.current ? this.current.price * qty : 0
    const thr = this.shipThreshold(cart)
    const below = thr > 0 && inCart + main < thr
    const text = below ? h.dataset.below : h.dataset.above
    if (text && h.textContent !== text) h.textContent = text
  }

  updateShipping () {
    this.updateAddonHeading()
    const box = this.querySelector('[data-bf-ship]')
    if (!box) return
    const sel = this.selectionTotal()
    const cart = getLastCart()
    const inCart = cart ? cart.total_price : 0
    const text = box.querySelector('[data-bf-ship-text]')
    const bar = box.querySelector('[data-bf-ship-bar]')
    const thr = this.shipThreshold(cart)
    if (thr > 0 && text) {
      const rest = thr - (inCart + sel)
      text.textContent = rest > 0
        ? String(box.dataset.progress || '').replace('{rest}', money(rest))
        : box.dataset.done || ''
      box.classList.toggle('is-done', rest <= 0)
      if (bar) bar.style.width = Math.min(100, Math.round((inCart + sel) / thr * 100)) + '%'
    }
    const pearls = box.querySelector('[data-bf-pearls]')
    if (pearls) {
      const n = Math.ceil(sel / 100 * this.ppe)
      pearls.textContent = box.dataset.showPearls === 'true' && n > 0 ? String(box.dataset.pearls || '').replace('{perlen}', n) : ''
    }
  }

  updateDelivery () {
    const box = this.querySelector('[data-bf-delivery-note]')
    if (!box) return
    const out = box.querySelector('[data-bf-delivery-text]')
    const available = this.current && this.current.available
    // R77: bei Vorbestellung steht der Termin ueber dem Button – Lieferhinweis ausblenden
    box.hidden = Boolean(this.laterDate())
    if (box.hidden) return
    box.classList.toggle('is-soldout', !available)
    out.textContent = ''
    const strong = document.createElement('strong')
    if (!available) {
      strong.textContent = box.dataset.soldOut || ''
      out.appendChild(strong)
      return
    }
    const now = getServerNow()
    const first = cal.earliest(now)
    let when = ''
    if (first) {
      const tomorrow = addDays(zonedNow(now, cal.config.timeZone).today, 1)
      when = toISO(first.delivery) === toISO(tomorrow)
        ? box.dataset.tomorrow
        : String(box.dataset.onDate || '').replace('{datum}', toLabel(first.delivery))
    }
    strong.textContent = [box.dataset.inStock, when].filter(Boolean).join(' ')
    out.appendChild(strong)
    const mins = cal.minutesUntilCutoff(now)
    let rest = box.dataset.wish || ''
    if (mins !== null && mins > 0 && box.dataset.countdown) {
      const cd = box.dataset.countdown.replace('{zeit}', formatDuration(mins))
      rest = `${cd} ${rest}`.trim()
    } else if (rest) {
      rest = rest.charAt(0).toUpperCase() + rest.slice(1)
    }
    if (rest) out.appendChild(document.createTextNode(' ' + rest))
  }

  /* ---------- Galerie ---------- */

  setupGallery () {
    const stage = this.querySelector('.bf-gallery__stage')
    if (!stage) return
    let startX = null
    stage.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX }, { passive: true })
    stage.addEventListener('touchend', (e) => {
      if (startX === null) return
      const dx = e.changedTouches[0].clientX - startX
      startX = null
      if (Math.abs(dx) < 40) return
      this.stepMedia(dx < 0 ? 1 : -1)
    })
  }

  stepMedia (dir) {
    const slides = Array.from(this.querySelectorAll('.bf-gallery__slide'))
    const idx = slides.findIndex((s) => s.classList.contains('is-active'))
    const next = slides[(idx + dir + slides.length) % slides.length]
    if (next) this.showMedia(next.dataset.mediaId)
  }

  showMedia (id) {
    const slides = this.querySelectorAll('.bf-gallery__slide')
    let found = false
    slides.forEach((s) => { if (s.dataset.mediaId === String(id)) found = true })
    if (!found) return
    slides.forEach((s) => {
      const on = s.dataset.mediaId === String(id)
      s.classList.toggle('is-active', on)
      s.hidden = !on
    })
    this.querySelectorAll('.bf-gallery__thumb').forEach((t) => {
      const on = t.dataset.mediaId === String(id)
      t.classList.toggle('is-active', on)
      if (on) {
        t.setAttribute('aria-current', 'true')
        t.scrollIntoView({ block: 'nearest', inline: 'nearest' })
      } else t.removeAttribute('aria-current')
    })
  }

  /* ---------- In den Warenkorb ---------- */

  async onSubmit (e) {
    e.preventDefault()
    if (!this.current || !this.current.available || this.busy) return
    const qty = this.qtyInput ? Math.max(1, parseInt(this.qtyInput.value, 10) || 1) : 1
    const items = [{ id: this.current.id, quantity: qty }]
    this.querySelectorAll('[data-bf-addon]:checked').forEach((cb) => items.push({ id: Number(cb.value), quantity: 1 }))
    const error = this.querySelector('[data-bf-error]')
    if (error) error.hidden = true
    this.busy = true
    if (this.atc) this.atc.classList.add('is-loading')
    try {
      const res = await fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data.status) {
        if (error) {
          error.textContent = data.description || data.message || 'Das hat leider nicht geklappt. Bitte versuche es noch einmal.'
          error.hidden = false
        }
        return
      }
      this.querySelectorAll('[data-bf-addon]:checked').forEach((cb) => { cb.checked = false })
      this.updateTotals()
      const drawer = window.BF && window.BF.cartDrawer
      if (drawer) {
        await drawer.refresh()
        drawer.open()
      } else {
        window.location.href = root() + 'cart'
      }
    } catch (err) {
      if (error) {
        error.textContent = 'Das hat leider nicht geklappt. Bitte versuche es noch einmal.'
        error.hidden = false
      }
    } finally {
      this.busy = false
      if (this.atc) this.atc.classList.remove('is-loading')
    }
  }

  /* ---------- Benachrichtigen bei Wiederverfuegbarkeit ---------- */

  variantName () {
    if (!this.current) return ''
    if (this.variants.length === 1 && this.current.title === 'Default Title') return (this.notify && this.notify.dataset.name) || ''
    return this.current.title
  }

  updateNotify () {
    const box = this.notify
    if (!box || !this.current) return
    const posted = box.querySelector('[data-bf-notify-posted]')
    box.hidden = this.current.available && !posted
    const name = this.variantName()
    const variant = box.querySelector('[data-bf-notify-variant]')
    if (variant) variant.value = this.current.title
    const link = box.querySelector('[data-bf-notify-link]')
    if (link) {
      try {
        const url = new URL(link.value)
        url.searchParams.set('variant', this.current.id)
        link.value = url.href
      } catch (e) { /* Link bleibt */ }
    }
    const text = box.querySelector('[data-bf-notify-text]')
    if (text && box.dataset.text) text.textContent = box.dataset.text.split('{variante}').join(name)
    // Nach einem Wechsel der Variante wieder das Formular zeigen
    const fields = box.querySelector('[data-bf-notify-fields]')
    const ok = box.querySelector('[data-bf-notify-ok]')
    if (fields && ok && this.notifiedId !== this.current.id) {
      fields.hidden = false
      ok.hidden = true
    }
    if (!box.hidden) this.checkOmnisend()
  }

  /** R65: Omnisend-Formular "Back in Stock" (eingebettet). Laedt es nicht, nach kurzer Zeit das Kontaktformular zeigen.
      R66: Omnisend zeigt das Formular nur fuer die ausverkaufte Variante aus der Adresse beim Seitenaufruf. Wird eine
      andere ausverkaufte Variante gewaehlt, laedt die Seite mit dieser Variante neu (Scrollposition bleibt). */
  checkOmnisend () {
    const box = this.notify
    const wrap = box && box.querySelector('[data-bf-notify-omnisend]')
    const fallback = box && box.querySelector('[data-bf-notify-fallback]')
    const loading = box && box.querySelector('[data-bf-notify-loading]')
    if (!wrap || !fallback) return
    if (loading) loading.hidden = true
    // Kontaktformular wurde schon abgeschickt (Seite nach dem Absenden): Bestaetigung zeigen
    if (box.querySelector('[data-bf-notify-posted]')) {
      wrap.hidden = true
      fallback.hidden = false
      return
    }
    const rendered = () => {
      const el = wrap.firstElementChild
      return !!(el && (el.childElementCount > 0 || el.offsetHeight > 24))
    }
    if (!this.omniObserver && window.MutationObserver) {
      this.omniObserver = new MutationObserver(() => { if (rendered() && String(this.current.id) === this.omniInitial) fallback.hidden = true })
      this.omniObserver.observe(wrap, { childList: true, subtree: true })
    }
    clearTimeout(this.omniTimer)
    clearTimeout(this.omniReload)
    const id = this.current ? String(this.current.id) : ''
    if (id && id !== this.omniInitial) {
      // Andere Variante als beim Seitenaufruf: Formular gehoert nicht zu dieser Variante
      wrap.hidden = true
      fallback.hidden = true
      if (loading) loading.hidden = false
      this.omniReload = setTimeout(() => {
        if (!this.current || String(this.current.id) !== id || box.hidden) return
        this.reloadForVariant(id, fallback, loading)
      }, 400)
      return
    }
    wrap.hidden = false
    if (rendered()) {
      fallback.hidden = true
      return
    }
    this.omniTimer = setTimeout(() => { if (!rendered()) fallback.hidden = false }, 3000)
  }

  reloadForVariant (id, fallback, loading) {
    let count = 0
    try { count = parseInt(window.sessionStorage.getItem('bf-bis-count'), 10) || 0 } catch (e) {}
    // Sicherung gegen Endlosschleifen: hoechstens 8 automatische Neuladungen je Sitzung, danach Kontaktformular
    if (count >= 8) {
      if (loading) loading.hidden = true
      if (fallback) fallback.hidden = false
      return
    }
    try {
      window.sessionStorage.setItem('bf-bis-count', String(count + 1))
      window.sessionStorage.setItem('bf-bis-scroll', JSON.stringify({ id, y: window.scrollY }))
    } catch (e) {}
    const url = new URL(window.location.href)
    url.searchParams.set('variant', id)
    url.hash = ''
    window.location.replace(url.toString())
  }

  restoreBisScroll () {
    let data = null
    try {
      data = JSON.parse(window.sessionStorage.getItem('bf-bis-scroll') || 'null')
      window.sessionStorage.removeItem('bf-bis-scroll')
    } catch (e) { data = null }
    if (!data || !this.current || String(data.id) !== String(this.current.id)) return
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual'
    const go = () => window.scrollTo(0, data.y || 0)
    go()
    window.requestAnimationFrame(go)
    window.addEventListener('load', go, { once: true })
  }

  async onNotify (e, form) {
    e.preventDefault()
    if (this.notifyBusy) return
    if (form.reportValidity && !form.reportValidity()) return
    const box = this.notify
    const err = box.querySelector('[data-bf-notify-error]')
    const btn = form.querySelector('button[type="submit"]')
    if (err) err.hidden = true
    this.notifyBusy = true
    if (btn) btn.setAttribute('aria-disabled', 'true')
    try {
      const res = await fetch(form.action, { method: 'POST', body: new URLSearchParams(new FormData(form)), credentials: 'same-origin' })
      // Spam-Schutz von Shopify verlangt eine Bestaetigung: normal absenden
      if (res.url.indexOf('/challenge') !== -1) {
        HTMLFormElement.prototype.submit.call(form)
        return
      }
      if (res.ok && res.url.indexOf('contact_posted=true') !== -1) {
        const fields = box.querySelector('[data-bf-notify-fields]')
        const ok = box.querySelector('[data-bf-notify-ok]')
        if (fields && ok) {
          ok.textContent = (box.dataset.success || '').split('{variante}').join(this.variantName())
          fields.hidden = true
          ok.hidden = false
          this.notifiedId = this.current && this.current.id
          ok.setAttribute('tabindex', '-1')
          ok.focus({ preventScroll: true })
        }
        return
      }
      // Fehler (z. B. ungueltige Adresse): normal absenden, damit Shopify die Meldung zeigt
      HTMLFormElement.prototype.submit.call(form)
    } catch (error) {
      if (err) {
        err.textContent = 'Das hat leider nicht geklappt. Bitte versuche es noch einmal.'
        err.hidden = false
      }
    } finally {
      this.notifyBusy = false
      if (btn) btn.removeAttribute('aria-disabled')
    }
  }

  /* ---------- Kaufleiste (Handy und Desktop) ---------- */

  /* Scroll-Listener statt IntersectionObserver: Der Observer meldet sich nur beim
     Uebergang sichtbar/unsichtbar und verpasst Spruenge (Anker, schnelles Wischen).
     Sichtbar nur, wenn der Warenkorb-Button oben aus dem Bild ist (nicht davor) und die Variante kaufbar ist. */
  setupSticky () {
    const bar = this.querySelector('[data-bf-sticky]')
    if (!bar || !this.atc) return
    let queued = false
    const check = () => {
      queued = false
      const r = this.atc.getBoundingClientRect()
      const past = r.height > 0 && r.bottom < 0
      const show = past && !!(this.current && this.current.available)
      if (bar.hidden === show) {
        bar.hidden = !show
        document.documentElement.classList.toggle('bf-sticky-open', show)
      }
    }
    this.onScroll = () => {
      if (queued) return
      queued = true
      window.requestAnimationFrame(check)
    }
    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.onScroll, { passive: true })
    check()
  }
}

if (!customElements.get('bf-product')) customElements.define('bf-product', BfProduct)
